import searchMetadata from '@/data/editorial/search-metadata.json';
import toolLabels from '@/data/editorial/tool-labels.json';
import type { Metadata } from 'next';
import { LANGS } from './langs';

export const SITE_URL = 'https://la-studio.cc';
const JA_EN_ONLY = /^\/(blog|alternatives|editorial-policy)(\/|[?#]|$)/;
const APP_ROUTE = /^\/(editor|loop-forge|billing|account)(\/|[?#]|$)/;

export function publicPath(lang: string, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (APP_ROUTE.test(p)) return p;
  const locale = JA_EN_ONLY.test(p) && lang !== 'ja' ? 'en' : lang;
  return locale === 'ja' ? p : `/${locale}${p === '/' ? '' : p}`;
}

export function languageAlternates(path: string, langs: readonly string[] = LANGS) {
  const supported = JA_EN_ONLY.test(path) ? ['ja', 'en'] : langs;
  return {
    ...Object.fromEntries(supported.map(lang => [lang, SITE_URL + publicPath(lang, path)])),
    'x-default': SITE_URL + publicPath('en', path),
  };
}

/** Next replaces nested Open Graph metadata; each page must supply a complete set. */
export function completePublicMetadata(lang: string, path: string, metadata: Metadata): Metadata {
  const override = (searchMetadata as Record<string, Record<string, { title?: string; description: string }>>)[lang]?.[path];
  const canonical = metadata.alternates?.canonical ?? SITE_URL + publicPath(lang, path);
  const originalTitle = typeof metadata.title === 'string' ? metadata.title
    : metadata.title && 'absolute' in metadata.title ? metadata.title.absolute
    : 'LA Studio';
  const labels = (toolLabels as Record<string, Record<string, string>>)[lang];
  const online: Record<string, string> = { en: 'Online', de: 'Online', es: 'Online', fr: 'en ligne', pt: 'Online', ja: 'オンライン', ko: '온라인', zh: '在线' };
  const compactToolTitle = originalTitle.length > 60 && labels?.[path.slice(1)]
    ? `${labels[path.slice(1)]} ${online[lang] ?? ''} | LA Studio` : undefined;
  // Avoid adding a second brand name or making a complete article title too long.
  const title = override?.title ?? compactToolTitle ?? (originalTitle.includes('LA Studio') || originalTitle.length > 47
    ? originalTitle : `${originalTitle} | LA Studio`);
  const description = override?.description ?? metadata.description;

  const noindex = typeof metadata.robots === 'object' && metadata.robots?.index === false;
  const locales: Record<string, string> = {ja:'ja_JP',en:'en_US',ko:'ko_KR',zh:'zh_CN',es:'es_ES',fr:'fr_FR',de:'de_DE',pt:'pt_BR'};
  const openGraph = {
    type: 'website' as const,
    siteName: 'LA Studio',
    locale: locales[lang] ?? 'en_US',
    images: [{ url: `${SITE_URL}/hero-screenshot.png`, width: 1200, height: 630 }],
    ...metadata.openGraph,
    url: typeof canonical === 'string' || canonical instanceof URL ? canonical : canonical.url,
    title,
    description: description ?? undefined,
  };
  return {
    ...metadata,
    title: { absolute: title },
    description,
    alternates: {
      ...metadata.alternates,
      canonical,
      languages: noindex ? undefined : metadata.alternates?.languages
        ? { ...metadata.alternates.languages, 'x-default': metadata.alternates.languages['x-default'] ?? SITE_URL + publicPath('en', path) }
        : undefined,
    },
    openGraph,
    // Nested metadata replaces the layout defaults. A blog's card must use
    // its own image and text instead of inheriting the homepage screenshot.
    twitter: {
      card: 'summary_large_image',
      images: openGraph.images,
      ...metadata.twitter,
      title,
      description: description ?? undefined,
    },
  };
}
